import GlobalStyles from '@mui/material/GlobalStyles';
import {
  memo,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { radii } from '../../theme/tokens';
import { getUiTokens } from '../../theme/uiTokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { Box } from './Box';
import { formatChartDateLabel } from './chartDateLabel';
import { resolveChartPlotAreaBackgroundColor } from './chartPlotAreaBackgroundColor';
import { buildChartPlotAreaFillSx } from './chartPlotAreaLayers';
import { calculateChartPlotBounds } from './chartPlotBounds';
import { buildAdaptiveTickLabelInterval } from './chartTickLabelInterval';
import { darken } from './colorUtils';
import { useTheme } from './hooks';
import { type StackedBarChartProps, type StackedBarSeries } from './StackedBarChart.types';
import { buildStackedBarChartAxisLineSx } from './stackedBarChartAxisLineSx';
import { formatStackedBarTooltipValue, formatWholeNumber } from './stackedBarChartFormatters';
import { StackedBarChartRoundedTopBar } from './StackedBarChartRoundedTopBar';
import { StackedTotalsTooltip } from './StackedBarChartTooltip';
import {
  buildStackedBarChartGlobalTooltipStyles,
  buildStackedBarChartTooltipSx,
} from './stackedBarChartTooltipStyles';
import { useRecentChartItemSelection } from './useRecentChartItemSelection';
import { useSyncStackedBarChartHorizontalGridLines } from './useSyncStackedBarChartHorizontalGridLines';
import { MuiBarChart } from './xCharts';
import type { BarItemIdentifier, ChartsAxisData } from '@mui/x-charts/models';

export type { StackedBarChartDatum } from './StackedBarChart.types';

const calculateNiceAxisStep = (value: number): number => {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const exponent = Math.floor(Math.log10(value));
  const magnitude = 10 ** exponent;
  const normalized = value / magnitude;

  if (normalized <= 1) return magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 5) return 5 * magnitude;
  return 10 * magnitude;
};

const calculateNiceStackedYAxisMax = (value: number, headroomTicks = 0): number => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const step = calculateNiceAxisStep(value / 3);
  const niceMax = Math.ceil(value / step) * step;
  if (headroomTicks <= 0 || niceMax > value) return niceMax;
  return niceMax + step * headroomTicks;
};

const STACKED_BAR_CHART_ANIMATION_DURATION_MS = 300;
const STACKED_BAR_CHART_ANIMATION_EASING = 'cubic-bezier(0.66, 0, 0.34, 1)';
const STACKED_BAR_CHART_PLOT_FILL_KEYFRAMES = 'flowqa-stacked-plot-fill';

const StackedBarChartBase = ({
  data,
  series,
  height = 260,
  xAxisLabelFormatter = formatChartDateLabel,
  xAxisTickLabelInterval,
  tooltipLabelFormatter = formatChartDateLabel,
  hideXAxisTicks = false,
  showAllXAxisLabels = false,
  xAxisLabelOffset = 4,
  yAxisLabelOffset = -6,
  axisStrokeWidth = 1,
  marginLeft = -8,
  marginRight = 16,
  marginTop = 16,
  marginBottom = 14,
  yAxisWidth = 72,
  yAxisAutoWidth = false,
  yAxisAutoWidthPadding = 12,
  yAxisHeadroomTicks = 0,
  yAxisTopPaddingRatio = 0.12,
  plotAreaBackgroundColor,
  showPlotAreaFrame = false,
  showPlotAreaTopFrame = true,
  barCornerRadius = radii.md,
  stackId = 'total',
  skipAnimation = false,
  animateDataChanges = false,
  showHorizontalGrid = true,
  showVerticalGrid = false,
  hideXAxisLine = false,
  hideYAxisLine = false,
  onSelectBar,
  disableInteraction = false,
  disableTooltip = false,
  tooltipEmptyValueLabel = '',
}: StackedBarChartProps): JSX.Element => {
  const categoryGapRatio = chartBarSizing.default.categoryGapRatio;
  const barGapRatio = chartBarSizing.default.barGapRatio;
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [plotBounds, setPlotBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const resizeResetTimerRef = useRef<number | null>(null),
    hasRenderedNonEmptyDataRef = useRef(false),
    hasRenderedPlotAreaFillRef = useRef(false);
  const [suspendAnimationForResize, setSuspendAnimationForResize] = useState(false);
  const [resolvedYAxisWidth, setResolvedYAxisWidth] = useState(yAxisWidth);
  const tokens = getUiTokens(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const resolvedPlotAreaBackgroundColor =
    plotAreaBackgroundColor ?? resolveChartPlotAreaBackgroundColor(theme);
  const barRadius = barCornerRadius;
  const { markRecentItemSelection, shouldSuppressAxisSelection } = useRecentChartItemSelection();
  const handleItemClick = useCallback(
    (_event: ReactMouseEvent<SVGElement>, item: BarItemIdentifier) => {
      const dataIndex = item.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const seriesKey = item.seriesId ? String(item.seriesId) : undefined;
      markRecentItemSelection(dataIndex);
      onSelectBar?.(dataIndex, seriesKey);
    },
    [markRecentItemSelection, onSelectBar],
  );
  const handleAxisClick = useCallback(
    (_event: MouseEvent, axisData: ChartsAxisData | null) => {
      const target = _event.target;
      if (target && target instanceof Element && target.closest('.MuiBarElement-root')) {
        return;
      }
      const dataIndex = axisData?.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      if (shouldSuppressAxisSelection(dataIndex)) {
        return;
      }
      onSelectBar?.(dataIndex);
    },
    [onSelectBar, shouldSuppressAxisSelection],
  );
  const palette = useMemo(
    () => [
      tokens.statusColors.purple,
      tokens.statusColors.green,
      tokens.statusColors.orange,
      tokens.statusColors.blue,
      tokens.statusColors.red,
    ],
    [
      tokens.statusColors.blue,
      tokens.statusColors.green,
      tokens.statusColors.orange,
      tokens.statusColors.purple,
      tokens.statusColors.red,
    ],
  );
  const segmentSeparatorColor = theme.palette.background.paper,
    segmentSeparatorWidth = 1;
  const resolveBarColor = useCallback(
    (base: string): string => (theme.palette.mode === 'dark' ? darken(base, 0.26) : base),
    [theme.palette.mode],
  );
  const tooltipFontSize = theme.typography.subtitle1.fontSize ?? '15px',
    tooltipLineHeight = theme.typography.subtitle1.lineHeight ?? 1.4,
    tooltipFontWeight = theme.typography.fontWeightRegular ?? 400;
  const rawLabels = useMemo(() => data.map((item) => String(item.label)), [data]);
  const xLabels = useMemo(
    () => rawLabels.map((label) => xAxisLabelFormatter(label)),
    [rawLabels, xAxisLabelFormatter],
  );
  const tooltipLabels = useMemo(
    () => data.map((item) => tooltipLabelFormatter(String(item.label))),
    [data, tooltipLabelFormatter],
  );
  const estimatedXAxisWidth =
    typeof containerWidth === 'number'
      ? Math.max(0, containerWidth - resolvedYAxisWidth - marginRight - Math.abs(marginLeft))
      : null;
  const xTickLabelInterval =
    xAxisTickLabelInterval ??
    buildAdaptiveTickLabelInterval(xLabels.length, showAllXAxisLabels, estimatedXAxisWidth);
  const adjustedMarginLeft = marginLeft + (resolvedYAxisWidth - yAxisWidth);
  const seriesKeys = useMemo(() => series.map((item) => item.key), [series]);
  const stackedValueMax = data.reduce((maxValue, row) => {
    const rowTotal = seriesKeys.reduce((sum, key) => {
      const value = Number(row[key]);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    return rowTotal > maxValue ? rowTotal : maxValue;
  }, 0);
  const yAxisTopPadding = Math.max(
      0,
      (stackedValueMax > 0 ? stackedValueMax : 1) * yAxisTopPaddingRatio,
    ),
    rawYAxisMax = stackedValueMax + yAxisTopPadding,
    yAxisMax = calculateNiceStackedYAxisMax(rawYAxisMax, yAxisHeadroomTicks);
  const { totalsByLabel, tooltipLabelByAxisLabel } = useMemo(() => {
    const nextTotalsByLabel = new Map<string, number>();
    const nextTooltipLabelByAxisLabel = new Map<string, string>();
    data.forEach((row, index) => {
      const axisLabel = xLabels[index];
      const tooltipLabel = tooltipLabels[index];
      if (axisLabel && tooltipLabel) {
        nextTooltipLabelByAxisLabel.set(axisLabel, tooltipLabel);
      }
      const label = tooltipLabel;
      if (!label) return;
      const total = seriesKeys.reduce((sum, key) => {
        const value = Number(row[key]);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
      nextTotalsByLabel.set(label, total);
    });
    return {
      totalsByLabel: nextTotalsByLabel,
      tooltipLabelByAxisLabel: nextTooltipLabelByAxisLabel,
    };
  }, [data, seriesKeys, tooltipLabels, xLabels]);
  const topSeriesKeyByRow = useMemo(
    () =>
      data.map((row) => {
        let topKey: string | null = null;
        seriesKeys.forEach((key) => {
          const value = Number(row[key]);
          if (!Number.isFinite(value) || value === 0) return;
          topKey = key;
        });
        return topKey;
      }),
    [data, seriesKeys],
  );
  const resolveSeriesColor = useCallback(
    (item: StackedBarSeries, index: number): string =>
      resolveBarColor(
        item.color ??
          (item.colorKey ? tokens.statusColors[item.colorKey] : palette[index % palette.length]),
      ),
    [palette, resolveBarColor, tokens.statusColors],
  );
  const effectiveSkipAnimation =
    skipAnimation ||
    suspendAnimationForResize ||
    (!animateDataChanges && hasRenderedNonEmptyDataRef.current);
  const shouldAnimatePlotAreaFill =
    plotBounds !== null &&
    !skipAnimation &&
    !suspendAnimationForResize &&
    !hasRenderedPlotAreaFillRef.current;
  useEffect(() => {
    hasRenderedNonEmptyDataRef.current ||= data.length > 0 && series.length > 0;
  }, [data.length, series.length]);
  useEffect(() => {
    if (plotBounds !== null) {
      hasRenderedPlotAreaFillRef.current = true;
    }
  }, [plotBounds]);
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const updateWidth = (): void => {
      const nextWidth = container.clientWidth;
      setContainerWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
    };
    updateWidth();
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });
    resizeObserver.observe(container);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  useLayoutEffect(() => {
    if (!yAxisAutoWidth) {
      setResolvedYAxisWidth(yAxisWidth);
      return;
    }
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frameId = 0;
    const updateYAxisWidth = (): void => {
      const labels = container.querySelectorAll(
        '.MuiChartsAxis-directionY .MuiChartsAxis-tickLabel',
      );
      if (labels.length === 0) return;
      let maxLabelWidth = 0;
      labels.forEach((label) => {
        if (!(label instanceof SVGGraphicsElement)) return;
        const bounds = label.getBBox();
        if (Number.isFinite(bounds.width) && bounds.width > maxLabelWidth) {
          maxLabelWidth = bounds.width;
        }
      });
      const extraLeftGutter = Math.max(0, -marginLeft);
      const nextWidth = Math.max(
        yAxisWidth,
        Math.ceil(
          maxLabelWidth + Math.abs(yAxisLabelOffset) + yAxisAutoWidthPadding + extraLeftGutter,
        ),
      );
      setResolvedYAxisWidth((currentWidth) =>
        Math.abs(currentWidth - nextWidth) > 1 ? nextWidth : currentWidth,
      );
    };
    const scheduleUpdate = (): void => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updateYAxisWidth);
    };
    scheduleUpdate();
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(container);
    const svg = container.querySelector('svg');
    if (svg instanceof SVGSVGElement) resizeObserver.observe(svg);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
    };
  }, [
    data,
    marginLeft,
    series,
    yAxisAutoWidth,
    yAxisAutoWidthPadding,
    yAxisLabelOffset,
    yAxisWidth,
  ]);
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let previousWidth = element.clientWidth;
    let previousHeight = element.clientHeight;
    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      const width = entry.contentRect.width;
      const heightValue = entry.contentRect.height;
      if (width === previousWidth && heightValue === previousHeight) return;
      previousWidth = width;
      previousHeight = heightValue;
      setSuspendAnimationForResize(true);
      if (resizeResetTimerRef.current !== null) window.clearTimeout(resizeResetTimerRef.current);
      resizeResetTimerRef.current = window.setTimeout(() => {
        setSuspendAnimationForResize(false);
        resizeResetTimerRef.current = null;
      }, 180);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (resizeResetTimerRef.current !== null) {
        window.clearTimeout(resizeResetTimerRef.current);
        resizeResetTimerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frameId = 0;
    const updatePlotBounds = (): void => {
      const svg = container.querySelector('svg');
      if (!(svg instanceof SVGSVGElement)) return;
      const yAxisLine = container.querySelector(
        '.MuiChartsAxis-directionY .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      const xAxisLine = container.querySelector(
        '.MuiChartsAxis-directionX .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      const xAxisTicks = Array.from(
        container.querySelectorAll('.MuiChartsAxis-directionX .MuiChartsAxis-tick'),
      ).filter((node): node is SVGGraphicsElement => node instanceof SVGGraphicsElement);
      const yAxisTickLabels = Array.from(
        container.querySelectorAll('.MuiChartsAxis-directionY .MuiChartsAxis-tickLabel'),
      ).filter((node): node is SVGGraphicsElement => node instanceof SVGGraphicsElement);
      const nextPlotBounds = calculateChartPlotBounds({
        marginTop,
        svgRect: svg.getBoundingClientRect(),
        xAxisRect: xAxisLine?.getBoundingClientRect() ?? null,
        xAxisTickRects: xAxisTicks.map((tick) => tick.getBoundingClientRect()),
        yAxisRect: yAxisLine?.getBoundingClientRect(),
        yAxisTickLabelRects: yAxisTickLabels.map((label) => label.getBoundingClientRect()),
      });
      setPlotBounds((currentBounds) => {
        if (nextPlotBounds === null) {
          return currentBounds;
        }
        if (
          currentBounds !== null &&
          currentBounds.left === nextPlotBounds.left &&
          currentBounds.top === nextPlotBounds.top &&
          currentBounds.width === nextPlotBounds.width &&
          currentBounds.height === nextPlotBounds.height
        ) {
          return currentBounds;
        }
        return nextPlotBounds;
      });
    };
    const scheduleUpdate = (): void => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updatePlotBounds);
    };
    scheduleUpdate();
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(container);
    const svg = container.querySelector('svg');
    if (svg instanceof SVGSVGElement) {
      resizeObserver.observe(svg);
    }
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [data, height, marginTop, resolvedYAxisWidth, series, yAxisWidth]);
  useSyncStackedBarChartHorizontalGridLines({ containerRef, showHorizontalGrid });
  const chartSeries = useMemo(
    () =>
      series.map((item, index) => ({
        id: item.key,
        data: data.map((row) => Number(row[item.key])),
        label: item.label,
        color: resolveSeriesColor(item, index),
        stack: stackId,
        valueFormatter: (value: number | null) => formatStackedBarTooltipValue(value),
      })),
    [data, resolveSeriesColor, series, stackId],
  );
  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: theme.app.colors.surface,
        color: theme.app.colors.divider,
        '& .MuiChartsSurface-root': { color: theme.app.colors.divider, overflow: 'visible' },
        '& .MuiChartsSurface-root svg': { overflow: 'visible' },
      }}
    >
      {plotBounds ? (
        <Box
          sx={[
            buildChartPlotAreaFillSx({
              backgroundColor: resolvedPlotAreaBackgroundColor,
              inset: 0,
              plotBounds,
            }),
            shouldAnimatePlotAreaFill
              ? {
                  transformOrigin: 'bottom',
                  animation: `${STACKED_BAR_CHART_PLOT_FILL_KEYFRAMES} ${STACKED_BAR_CHART_ANIMATION_DURATION_MS}ms ${STACKED_BAR_CHART_ANIMATION_EASING} both`,
                  [`@keyframes ${STACKED_BAR_CHART_PLOT_FILL_KEYFRAMES}`]: {
                    from: { transform: 'scaleY(0)' },
                    to: { transform: 'scaleY(1)' },
                  },
                }
              : {},
          ]}
        />
      ) : null}
      {showPlotAreaFrame && plotBounds ? (
        <Box
          sx={{
            position: 'absolute',
            left: plotBounds.left + (hideYAxisLine ? axisStrokeWidth / 2 : 0),
            top: plotBounds.top,
            width:
              plotBounds.width + axisStrokeWidth / 2 - (hideYAxisLine ? axisStrokeWidth / 2 : 0),
            height: plotBounds.height,
            pointerEvents: 'none',
            zIndex: 0,
            boxShadow: [
              ...(hideYAxisLine
                ? [`inset ${axisStrokeWidth}px 0 0 ${theme.app.colors.divider}`]
                : []),
              `inset -${axisStrokeWidth}px 0 0 ${theme.app.colors.divider}`,
              ...(showPlotAreaTopFrame
                ? [`inset 0 ${axisStrokeWidth}px 0 ${theme.app.colors.divider}`]
                : []),
            ].join(', '),
          }}
          aria-hidden="true"
        />
      ) : null}
      <GlobalStyles styles={buildStackedBarChartGlobalTooltipStyles(theme)} />
      <MuiBarChart
        height={height}
        hideLegend
        skipAnimation={effectiveSkipAnimation}
        borderRadius={barRadius}
        slots={{
          bar: (props) => (
            <StackedBarChartRoundedTopBar
              {...props}
              topSeriesKeyByRow={topSeriesKeyByRow}
              barRadius={barRadius}
              segmentSeparatorColor={segmentSeparatorColor}
              segmentSeparatorWidth={segmentSeparatorWidth}
            />
          ),
          tooltip: (tooltipProps) => (
            <StackedTotalsTooltip
              {...tooltipProps}
              totalsByLabel={totalsByLabel}
              tooltipLabelByAxisLabel={tooltipLabelByAxisLabel}
              tooltipLabelsByIndex={tooltipLabels}
              tooltipLabelFormatter={tooltipLabelFormatter}
              tooltipEmptyValueLabel={tooltipEmptyValueLabel}
            />
          ),
        }}
        onItemClick={onSelectBar && !disableInteraction ? handleItemClick : undefined}
        onAxisClick={onSelectBar && !disableInteraction ? handleAxisClick : undefined}
        xAxis={[
          {
            data: rawLabels,
            scaleType: 'band',
            categoryGapRatio,
            barGapRatio,
            tickPlacement: 'extremities',
            tickLabelPlacement: 'middle',
            valueFormatter: (value) => xAxisLabelFormatter(String(value)),
            tickLabelInterval: xTickLabelInterval,
            tickLabelStyle: {
              fill: theme.app.colors.textMuted,
              fontSize: '13px',
              transform: `translateY(${xAxisLabelOffset}px)`,
            },
            tickSize: hideXAxisTicks ? 0 : 6,
          },
        ]}
        yAxis={[
          {
            min: 0,
            max: yAxisMax > 0 ? yAxisMax : undefined,
            width: resolvedYAxisWidth,
            tickLabelStyle: {
              fill: theme.app.colors.textMuted,
              fontSize: '13px',
              transform: `translateX(${yAxisLabelOffset}px)`,
            },
            tickSize: 0,
            valueFormatter: formatWholeNumber,
          },
        ]}
        grid={{ horizontal: showHorizontalGrid, vertical: showVerticalGrid }}
        margin={{
          left: adjustedMarginLeft,
          right: marginRight,
          top: marginTop,
          bottom: marginBottom,
        }}
        slotProps={{
          axisLine: { stroke: theme.app.colors.divider },
          axisTick: { stroke: theme.app.colors.divider },
          tooltip: {
            classes: {
              paper: 'flowqa-stacked-tooltip-paper',
              table: 'flowqa-stacked-tooltip-table',
              cell: 'flowqa-stacked-tooltip-cell',
              mark: 'flowqa-stacked-tooltip-mark',
            },
            sx: buildStackedBarChartTooltipSx({
              borderColor: theme.app.colors.border,
              textColor: theme.app.colors.text,
              tooltipFontSize,
              tooltipFontWeight,
              tooltipLineHeight,
            }),
          },
        }}
        sx={{
          position: 'relative',
          zIndex: 1,
          '& .MuiChartsAxis-tickLabel': { fill: theme.app.colors.textMuted },
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
            strokeWidth: axisStrokeWidth,
          },
          '& .MuiChartsGrid-horizontalLine': {
            stroke: theme.app.colors.divider,
            strokeWidth: '0.75px',
          },
          '& .MuiChartsAxis-line': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
            strokeWidth: axisStrokeWidth,
          },
          '& .MuiChartsAxis-line[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          '& .MuiChartsAxis-tick[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
            strokeWidth: `${axisStrokeWidth}px !important`,
          },
          ...buildStackedBarChartAxisLineSx({
            dividerColor: theme.app.colors.divider,
            hideXAxisLine,
            hideYAxisLine,
          }),
          '& .MuiChartsAxisHighlight-root, & .MuiChartsAxisHighlight': {
            pointerEvents: 'none',
          },
          '& .MuiChartsAxisHighlight-root *': {
            pointerEvents: 'none',
          },
          '& .MuiChartsAxisHighlight-rect, & .MuiChartsAxisHighlightRect': {
            fill: 'transparent !important',
            opacity: 0,
          },
          '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlightLine': {
            stroke: 'transparent !important',
            opacity: 0,
          },
          ...(onSelectBar && !disableInteraction
            ? {
                '& .MuiChartsAxis-directionX': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsOverlay-root': { cursor: 'pointer' },
                '& svg': { cursor: 'pointer' },
              }
            : null),
          ...(disableInteraction
            ? {
                '& .MuiChartsOverlay-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight': { pointerEvents: 'none' },
                '& .MuiBarElement-root': { pointerEvents: 'none' },
              }
            : null),
          ...(disableTooltip
            ? {
                '& .MuiChartsTooltip-root': { display: 'none' },
                '& .MuiChartsTooltip-paper': { display: 'none' },
              }
            : null),
        }}
        series={chartSeries}
      />
    </Box>
  );
};
export const StackedBarChart = memo(StackedBarChartBase);
